/** Live operator presence board — polls /v1/operators/availability.
 *
 * Reusable: dropped on the dashboard, the member view, the operators tab,
 * and the campaign-detail overview. Shows who's in a call now (and whether
 * that call is within their schedule), who's available (on-shift + free),
 * off-shift, or has no schedule. Renders nothing without org.member.read.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { useHasPermission } from "@/lib/permissions";

type PresenceStatus = "in_call" | "available" | "off_shift" | "no_schedule";

type OperatorPresence = {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  status: PresenceStatus;
  in_schedule: boolean | null;
  current_session: {
    id: string;
    started_at: string | null;
    title: string;
  } | null;
};

type PresenceResponse = {
  as_of: string;
  operators: OperatorPresence[];
  summary: Record<PresenceStatus, number>;
};

async function fetchOperatorAvailability(): Promise<PresenceResponse> {
  const res = await api.get<PresenceResponse>(ApiPaths.operatorAvailability);
  return res.data;
}

const DOT: Record<PresenceStatus, string> = {
  in_call: "bg-nb-mint",
  available: "bg-emerald-500",
  off_shift: "bg-nb-text-3",
  no_schedule: "bg-amber-500",
};

const STATUS_KEY: Record<PresenceStatus, string> = {
  in_call: "admin.operators.presence.inCall",
  available: "admin.operators.presence.available",
  off_shift: "admin.operators.presence.offShift",
  no_schedule: "admin.operators.presence.noSchedule",
};

function displayName(o: OperatorPresence): string {
  const n = `${o.first_name} ${o.last_name}`.trim();
  return n || o.email;
}

export function OperatorAvailabilityBoard({
  className = "",
}: {
  className?: string;
}) {
  const { t } = useTranslation();
  const canSee = useHasPermission("org.member.read");
  const q = useQuery({
    queryKey: ["operator-availability"],
    queryFn: fetchOperatorAvailability,
    refetchInterval: 20000,
    enabled: canSee,
  });

  if (!canSee) return null;

  const data = q.data;
  const summary = data?.summary;

  return (
    <div className={`card ${className}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-nb-text">
          {t("admin.operators.presence.title")}
        </h3>
        {summary && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-nb-text-2">
            {(
              ["in_call", "available", "off_shift", "no_schedule"] as const
            ).map((s) => (
              <span key={s} className="inline-flex items-center gap-1">
                <span className={`h-2 w-2 rounded-full ${DOT[s]}`} />
                {t(STATUS_KEY[s])}: {summary[s]}
              </span>
            ))}
          </div>
        )}
      </div>

      {q.isError && (
        <p className="text-xs text-cvp-danger">
          {t("admin.operators.presence.error")}
        </p>
      )}
      {data && data.operators.length === 0 && (
        <p className="text-xs text-nb-text-3">
          {t("admin.operators.presence.empty")}
        </p>
      )}
      {data && data.operators.length > 0 && (
        <ul className="divide-y divide-nb-border/60">
          {data.operators.map((o) => (
            <li
              key={o.user_id}
              className="flex items-center justify-between gap-3 py-1.5"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${DOT[o.status]}`}
                  title={t(STATUS_KEY[o.status])}
                />
                <span className="truncate text-sm text-nb-text" title={o.email}>
                  {displayName(o)}
                </span>
              </div>
              <div className="shrink-0 text-right text-xs text-nb-text-2">
                {t(STATUS_KEY[o.status])}
                {o.status === "in_call" && o.in_schedule !== null && (
                  <span
                    className={
                      o.in_schedule ? "ml-1 text-emerald-500" : "ml-1 text-amber-500"
                    }
                  >
                    ·{" "}
                    {o.in_schedule
                      ? t("admin.operators.presence.onShift")
                      : t("admin.operators.presence.offShiftFlag")}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
